import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Card, Button, SeverityPill, Toggle, fmt$, fmtN, dotColor, fmtDate } from "@/components/primitives";
import AccountSettingsPanel from "@/components/AccountSettingsPanel";

const REASONS = ["customer owns machine", "site closed or relocated", "seasonal",
  "price dispute", "switched to pods", "other"];
const OWN_LABEL = { rented: "rented", financed: "financed", owned: "owned outright", unknown: "ownership unknown" };
const SITE_STATUS_LABEL = { stopped: "no signal", never: "never ordered", unknown: "no orders in available history", no_machine: "no machine" };

function Modal({ title, children, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(4,6,15,.6)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={onClose}>
      <Card style={{ width: 460, padding: 26 }} onClick={(e) => e.stopPropagation()}>
        <div className="num" style={{ fontSize: 19, fontWeight: 500, color: "var(--ui-text-hi)", marginBottom: 16 }}>{title}</div>
        {children}
      </Card>
    </div>
  );
}

function BeanChart({ chart }) {
  if (!chart || chart.length === 0) return null;
  // Normalise against the largest of BOTH expected and actual across the window,
  // so actual bars are always visible even when an account has no baseline
  // (expected = 0). A track (expected) is drawn behind the actual fill.
  const maxVal = Math.max(
    ...chart.map((c) => Math.max(c.expected || 0, c.actual || 0)), 1);
  const H = 208;
  const hasExpected = chart.some((c) => (c.expected || 0) > 0);
  const hasActual = chart.some((c) => (c.actual || 0) > 0);
  const firstBreachIdx = chart.findIndex((c) => c.breach);
  return (
    <div>
      {!hasActual && (
        <div data-testid="chart-empty-note" className="prose-i"
          style={{ fontSize: 12, color: "var(--ui-text-lo)", marginBottom: 8 }}>
          No bean orders recorded in this window.
        </div>
      )}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 7, height: H }}>
        {chart.map((c, i) => {
          const trackH = (c.expected || 0) > 0
            ? Math.max(8, (c.expected / maxVal) * H) : H;
          const fillH = (c.actual || 0) > 0
            ? Math.max(4, Math.min(H, (c.actual / maxVal) * H)) : 0;
          if (c.noData) {
            // Pre-history: distinct hatched "no data" zone, never a zero bar.
            return (
              <div key={i} title={`${c.period} — no synced history`} style={{ flex: 1, height: H, alignSelf: "flex-end",
                borderRadius: 5, border: "1px dashed var(--ui-line)",
                background: "repeating-linear-gradient(45deg, var(--ui-bg) 0 4px, transparent 4px 8px)",
                opacity: 0.5 }} />
            );
          }
          return (
            <div key={i} style={{ flex: 1, height: H, position: "relative", alignSelf: "flex-end" }}>
              {/* track = baseline (expected) */}
              <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: trackH,
                background: (c.expected || 0) > 0 ? "var(--ui-track)" : "transparent",
                border: (c.expected || 0) > 0 ? "none" : "1px dashed var(--ui-line-soft)",
                borderRadius: 5 }} />
              {/* actual fill — NOT nested in the track, so overshoot is visible */}
              <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: fillH,
                borderRadius: 5,
                background: c.breach ? "var(--sv-crit)"
                  : "linear-gradient(to top, var(--ac-cyan), var(--ac-indigo))" }} />
            </div>
          );
        })}
      </div>
      <div className="flex" style={{ justifyContent: "space-between", marginTop: 12 }}>
        <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>{chart[0]?.period}</span>
        {firstBreachIdx >= 0 && hasExpected && (
          <span className="prose-i" style={{ fontSize: 11, color: "var(--sv-crit)", fontWeight: 600 }}>
            {chart[firstBreachIdx]?.period} — decline begins
          </span>
        )}
        <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>{chart[chart.length - 1]?.period}</span>
      </div>
    </div>
  );
}

/* One site's committed volume. Separate from the account figure on purpose:
   an account committed to 50 kg/month across five sites is not 50 kg per
   site, so the two are never mixed and a site is never given the account's
   number. A site left blank keeps its own history-derived baseline. */
function SiteCommitted({ cid, site }) {
  const qc = useQueryClient();
  const [val, setVal] = React.useState(
    site.committedKgPerPeriod == null ? "" : String(site.committedKgPerPeriod));
  const [busy, setBusy] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  const initial = site.committedKgPerPeriod == null ? "" : String(site.committedKgPerPeriod);
  const dirty = val.trim() !== initial;

  const save = async () => {
    setBusy(true);
    try {
      await endpoints.saveSiteSettings(cid, site.siteId, {
        // Blank means "no commitment" - the backend deletes the row and the
        // site reverts to its history baseline.
        committedKgPerPeriod: val.trim() === "" ? null : parseFloat(val),
        commitmentPeriod: "monthly",
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1600);
      await qc.invalidateQueries();
    } finally {
      setBusy(false);
    }
  };

  const box = {
    background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 8,
    padding: "5px 9px", color: "var(--ui-text-hi)", fontFamily: "'Space Grotesk',sans-serif",
    fontSize: 13, width: 74,
  };

  return (
    <div className="flex items-center" style={{ gap: 7, flexWrap: "wrap" }}>
      <span className="prose-i" style={{ fontSize: 10.5, textTransform: "uppercase",
        letterSpacing: "0.5px", color: "var(--ui-text-lo)" }}>Committed</span>
      <input
        data-testid={`site-committed-${site.siteId}`}
        type="number" min="0" step="0.1" value={val} style={box}
        placeholder={site.baselineMonthlyKg != null ? String(site.baselineMonthlyKg) : "\u2014"}
        title={site.committedKgPerPeriod == null
          ? "No commitment set - this site is measured against its own order history"
          : "Committed volume for this site"}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && dirty && !busy) save(); }} />
      <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>kg/mo</span>
      {dirty && (
        <button onClick={save} disabled={busy} className="prose-i"
          style={{ background: "var(--ac-indigo)", border: "none", borderRadius: 7,
            padding: "4px 11px", fontSize: 11, color: "#fff",
            cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
          {busy ? "Saving\u2026" : "Save"}
        </button>
      )}
      {saved && !dirty && (
        <span className="prose-i" style={{ fontSize: 11, color: "var(--sv-good)" }}>Saved \u2713</span>
      )}
      {!dirty && !saved && site.committedKgPerPeriod == null && (
        <span className="prose-i" style={{ fontSize: 10.5, color: "var(--ui-text-lo)", fontStyle: "italic" }}>
          {site.baselineSource === "none" || site.baselineMonthlyKg == null
            ? "no baseline yet"
            : "using history baseline"}
        </span>
      )}
    </div>
  );
}


export default function AccountDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["account", id], queryFn: () => endpoints.account(id) });
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [action, setAction] = useState("");
  const [err, setErr] = useState("");

  if (!data) return <div className="prose-i" style={{ color: "var(--ui-text-lo)" }}>Loading…</div>;
  const { account, signal, chart, recentOrders, actionLog, perSite = [] } = data;
  const sev = signal?.severity || "good";
  const sevColor = dotColor(sev);

  const submitSnooze = async () => {
    setErr("");
    if (!reason) { setErr("A dismissal reason is mandatory."); return; }
    try {
      await endpoints.dismiss(signal.id, { reason, note });
      setSnoozeOpen(false); setReason(""); setNote("");
      await qc.invalidateQueries();
      nav("/");
    } catch (e) { setErr(e?.response?.data?.detail || "Could not snooze"); }
  };
  const submitLog = async () => {
    if (!action.trim()) return;
    await endpoints.logAction(id, { action, note });
    setLogOpen(false); setAction(""); setNote("");
    await qc.invalidateQueries({ queryKey: ["account", id] });
  };

  const metaLine = [
    `${account.siteCount} ${account.siteCount === 1 ? "site" : "sites"}`,
    OWN_LABEL[account.ownership] || account.ownership,
    account.contractEndLabel ? `contract to ${account.contractEndLabel}` : null,
  ].filter(Boolean).join(" · ");

  const inputStyle = { width: "100%", background: "var(--ui-bg)", border: "1px solid var(--ui-line)",
    borderRadius: 10, padding: "11px 13px", color: "var(--ui-text-hi)", fontFamily: "'Inter', sans-serif", fontSize: 14, marginTop: 6 };

  return (
    <div>
      {/* header */}
      <button onClick={() => nav("/")} className="prose-i" data-testid="back-leakboard"
        style={{ background: "none", border: "none", color: "var(--ui-text-mid)", fontSize: 13, cursor: "pointer", marginBottom: 12 }}>
        ← Leak Board
      </button>
      <div className="flex" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div className="num" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.9px", color: "var(--ui-text-hi)" }}>{account.name}</div>
          <div className="flex items-center" style={{ gap: 10, marginTop: 10, flexWrap: "wrap" }}>
            {signal
              ? <SeverityPill severity={sev} label={signal.typeLabel} />
              : account.dataState === "insufficient_history"
                ? <span data-testid="detail-insufficient" className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)", border: "1px solid var(--ui-line)", background: "var(--ui-bg)", borderRadius: 8, padding: "3px 10px" }}>Insufficient history</span>
                : account.dataState === "no_data"
                  ? <span className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", border: "1px solid var(--ui-line)", background: "var(--ui-bg)", borderRadius: 8, padding: "3px 10px" }}>No orders yet</span>
                  : <SeverityPill severity={sev} />}
            <span className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)" }}>{metaLine}</span>
            {account.ownershipSource && (
              <span data-testid="ownership-source" className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", fontStyle: "italic" }}>
                · ownership: {account.ownershipSource}
              </span>
            )}
            {account.ownershipOverriddenManual && (
              <span data-testid="ownership-override" className="prose-i" style={{ fontSize: 11, color: "var(--sv-watch)", border: "1px solid var(--sv-watch)", background: "var(--sv-watch-bg)", borderRadius: 8, padding: "2px 8px" }}>
                Simpro overrode manual: {account.ownershipOverriddenManual}
              </span>
            )}
          </div>
        </div>
        <div className="flex" style={{ gap: 12 }}>
          <Button variant="secondary" data-testid="open-snapshot-btn" onClick={() => nav(`/snapshot/${account.id}`)}>Open Snapshot</Button>
          {signal && <Button variant="secondary" data-testid="snooze-btn" onClick={() => setSnoozeOpen(true)}>Snooze with reason</Button>}
          <Button data-testid="logcontact-btn" onClick={() => setLogOpen(true)}>Log contact</Button>
        </div>
      </div>

      {/* evidence banner */}
      {signal && (
        <div style={{ background: `var(--sv-${sev}-bg)`, border: `1px solid ${sevColor}`, borderRadius: 16,
          padding: "20px 24px", marginTop: 20, position: "relative", overflow: "hidden", display: "flex", alignItems: "center" }}
          data-testid="evidence-banner">
          <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: sevColor }} />
          <div style={{ flex: 1, paddingLeft: 8 }}>
            <div className="prose-i" style={{ fontSize: 16, fontWeight: 600, color: "var(--ui-text-hi)" }}>
              {signal.evidence?.headline}
            </div>
            <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-mid)", marginTop: 6 }}>
              {signal.signals?.map((s) => s.detail).slice(0, 2).join(" ")} Baseline: {account.baselineWorking || signal.baselineSource}.
            </div>
            {account.baselineVolatile && (
              <div className="prose-i" style={{ display: "inline-block", marginTop: 8, fontSize: 11.5, fontWeight: 600, color: "var(--sv-watch)", background: "var(--sv-watch-bg)", border: "1px solid var(--sv-watch)", borderRadius: 8, padding: "3px 9px" }}>
                Low-confidence baseline · {account.baselineVolatileReason || "volatile ordering"} — severity capped at At risk
              </div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="num" style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.7px", color: sevColor }}>{fmt$(signal.valueAtRisk)}</div>
            <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>annualised exposure</div>
          </div>
        </div>
      )}

      {/* exposure basis — every dollar traces to a source */}
      {signal?.exposureBasis && (
        <div data-testid="exposure-basis" style={{ marginTop: 10, background: "var(--ui-card-2)", border: "1px solid var(--ui-line-soft)", borderLeft: "3px solid var(--ac-indigo)", borderRadius: 10, padding: "12px 16px" }}>
          <span className="num" style={{ fontSize: 13.5, color: "var(--ui-text-hi)" }}>{signal.exposureBasis.text}</span>
          <span className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginLeft: 8 }}>
            · price basis: {signal.exposureBasis.priceSource === "customer" ? "this customer's realised average" : signal.exposureBasis.priceSource === "override" ? "manual override" : "portfolio fallback"}
            {" · "}× {signal.exposureBasis.annualisationFactor} = {fmt$(signal.valueAtRisk)}/yr
          </span>
        </div>
      )}

      {/* chart card */}
      <Card style={{ marginTop: 20, padding: 26 }}>
        <div className="flex" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
          <div>
            <div className="num" style={{ fontSize: 19, fontWeight: 500, color: "var(--ui-text-hi)" }}>Bean flow, {chart.length} months</div>
            <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3 }}>
              Track height = {account.contractEndLabel ? "contracted" : "baseline"} volume · fill = actual ordered
            </div>
          </div>
          <div className="flex" style={{ gap: 16 }}>
            <span className="flex items-center prose-i" style={{ gap: 6, fontSize: 12, color: "var(--ui-text-mid)" }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: "linear-gradient(to top,var(--ac-cyan),var(--ac-indigo))" }} /> Within tolerance</span>
            <span className="flex items-center prose-i" style={{ gap: 6, fontSize: 12, color: "var(--ui-text-mid)" }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: "var(--sv-crit)" }} /> Breach</span>
          </div>
        </div>
        <BeanChart chart={chart} />
      </Card>

      {/* §1.7 per-site breakdown — the company screen, but each site on its own terms */}
      {perSite.length > 0 && (
        <Card style={{ marginTop: 20, padding: 26 }}>
          <div className="flex items-center" style={{ justifyContent: "space-between" }}>
            <div>
              <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Sites</div>
              <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3 }}>
                {perSite.length} {perSite.length === 1 ? "site" : "sites"} · volume, flow and contacts per site
              </div>
            </div>
            {account.assessmentLevel === "company" && (
              <span className="prose-i" style={{ fontSize: 11.5, color: "var(--sv-watch)", fontStyle: "italic", maxWidth: 320, textAlign: "right" }}>
                Assessed at company level — individual sites order too infrequently to measure
              </span>
            )}
          </div>
          {perSite.map((s, i) => (
            <div key={s.siteId || i} style={{ padding: "16px 0", borderTop: "1px solid var(--ui-line-soft)", marginTop: i === 0 ? 14 : 0 }}>
              <div className="flex items-center" style={{ gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 220px", minWidth: 200 }}>
                  <div className="prose-i" style={{ fontSize: 14.5, fontWeight: 700, color: "var(--ui-text-hi)" }}>
                    {s.name}
                    {s.archived && <span className="prose-i" style={{ fontSize: 11, color: "var(--sv-watch)", marginLeft: 8, border: "1px solid var(--sv-watch)", borderRadius: 6, padding: "1px 6px" }}>closed / relocated</span>}
                  </div>
                  <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 2 }}>
                    {OWN_LABEL[s.ownership] || s.ownership} · {s.machineCount} machine{s.machineCount === 1 ? "" : "s"}
                  </div>
                </div>
                <div style={{ flex: "0 0 140px" }}>
                  <div className="num" style={{ fontSize: 18, fontWeight: 600, color: "var(--ui-text-hi)" }}>{fmtN(s.kg12m)} <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", fontWeight: 400 }}>kg</span></div>
                  <div className="prose-i" style={{ fontSize: 10.5, color: "var(--ui-text-lo)" }}>{s.kgWindow}</div>
                </div>
                <div style={{ flex: "0 0 90px" }}>
                  {s.archived
                    ? <span className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>closed / relocated</span>
                    : <div className="num" style={{ fontSize: 15, color: "var(--ui-text-hi)" }}>{s.daysDry ?? "—"}{s.daysDry != null && <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}> d dry</span>}</div>}
                </div>
                <div style={{ flex: "0 0 160px" }}>
                  {s.companyLevel
                    ? <span className="prose-i" style={{ fontSize: 11, color: "var(--sv-watch)", fontStyle: "italic" }}>at company level</span>
                    : s.severity
                      ? <SeverityPill severity={s.severity} label={s.typeLabel} />
                      : <span className="prose-i" style={{ fontSize: 11.5, color: s.orderStatus === "unknown" ? "var(--sv-watch)" : "var(--ui-text-lo)" }}>{SITE_STATUS_LABEL[s.orderStatus] || (s.everOrderedBeans ? "no signal" : "no orders")}</span>}
                </div>
              </div>
              {!s.archived && s.siteId && (
                <div style={{ marginTop: 10 }}>
                  <SiteCommitted cid={account.id} site={s} />
                </div>
              )}
              {(s.machines?.length > 0 || s.contacts?.length > 0) && (
                <div className="flex" style={{ gap: 30, marginTop: 10, flexWrap: "wrap" }}>
                  {s.machines?.length > 0 && (
                    <div style={{ flex: "1 1 240px" }}>
                      <div className="prose-i" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-lo)", marginBottom: 4 }}>Machines</div>
                      {s.machines.map((m, j) => (
                        <div key={j} className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-mid)" }}>{m.machine || "Machine"}{m.serial ? ` · SN ${m.serial}` : ""}</div>
                      ))}
                    </div>
                  )}
                  {s.contacts?.length > 0 && (
                    <div style={{ flex: "1 1 240px" }}>
                      <div className="prose-i" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-lo)", marginBottom: 4 }}>Contacts</div>
                      {s.contacts.map((ct, j) => (
                        <div key={j} className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-mid)" }}>
                          {ct.name}{ct.phone ? ` · ${ct.phone}` : ""}{ct.role ? <span style={{ color: "var(--ui-text-lo)" }}> · {ct.role}</span> : ""}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {/* three panels */}
      <div className="flex" style={{ gap: 20, marginTop: 20, alignItems: "flex-start" }}>
        {/* recent orders */}
        <Card style={{ flex: 1, padding: 24 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Recent orders</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 10 }}>bean-free orders highlighted</div>
          {recentOrders.length === 0 && <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)", padding: "10px 0" }}>No recent orders on record.</div>}
          {recentOrders.map((o, i) => (
            <div key={i} className="flex items-center" style={{ padding: "13px 0", gap: 12, borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)" }}>
              <span style={{ width: 3, alignSelf: "stretch", background: o.beanFree ? "var(--sv-crit)" : "var(--ui-line)", borderRadius: 4 }} />
              <div style={{ flex: 1 }}>
                <div className="prose-i" style={{ fontSize: 13.5, color: o.beanFree ? "var(--sv-crit)" : "var(--ui-text-hi)", fontWeight: 500 }}>{o.description}</div>
                <div className="num" style={{ fontSize: 11, color: "var(--ui-text-lo)", marginTop: 2 }}>{fmtDate(o.date)}</div>
              </div>
              <span className="num" style={{ fontSize: 14, color: "var(--ui-text-hi)" }}>{fmt$(o.amount)}</span>
            </div>
          ))}
        </Card>

        {/* action log */}
        <Card style={{ flex: 1, padding: 24 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Action log</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 10 }}>audit trail</div>
          {actionLog.length === 0 && <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)", padding: "10px 0" }}>No actions logged yet.</div>}
          {actionLog.map((a, i) => (
            <div key={i} className="flex" style={{ padding: "13px 0", gap: 12, borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)" }}>
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: dotColor(a.dot), marginTop: 5 }} />
              <div style={{ flex: 1 }}>
                <div className="prose-i" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ui-text-hi)" }}>{a.action}</div>
                <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 2 }}>{a.note}</div>
              </div>
              <span className="num" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>{fmtDate(a.date)}</span>
            </div>
          ))}
        </Card>
      </div>

      <AccountSettingsPanel cid={account.id} settings={data.accountSettings}
        suggestion={data.suggestion} source={account.ownership} />

      {/* Internal account flag — C4B's own machines (workshop, demo, staff kitchens) */}
      <Card style={{ marginTop: 20, padding: "18px 24px" }}>
        <div className="flex items-center" style={{ justifyContent: "space-between", gap: 20 }}>
          <div>
            <div className="prose-i" style={{ fontSize: 14, fontWeight: 600, color: "var(--ui-text-hi)" }}>Internal account</div>
            <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, maxWidth: 560 }}>
              C4B's own machines (workshop, demo units, staff kitchens). When on, this account is excluded from the leak board, working lists and exposure totals — but still counted on Data Health as an excluded internal account, so nothing vanishes silently.
            </div>
          </div>
          <Toggle data-testid="internal-toggle" on={!!account.internalAccount} onChange={async (v) => {
            await endpoints.setInternalAccount(account.id, v); await qc.invalidateQueries();
          }} />
        </div>
      </Card>

      {snoozeOpen && (
        <Modal title="Snooze with reason" onClose={() => setSnoozeOpen(false)}>
          <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)" }}>Reason (required)
            <select data-testid="snooze-reason" value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle}>
              <option value="">Select a reason…</option>
              {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <div style={{ height: 12 }} />
          <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)" }}>Note (optional)
            <textarea value={note} onChange={(e) => setNote(e.target.value)} style={{ ...inputStyle, minHeight: 70 }} />
          </label>
          {err && <div className="prose-i" style={{ color: "var(--sv-crit)", fontSize: 12.5, marginTop: 10 }}>{err}</div>}
          <div className="flex" style={{ gap: 12, marginTop: 18, justifyContent: "flex-end" }}>
            <Button variant="secondary" onClick={() => setSnoozeOpen(false)}>Cancel</Button>
            <Button data-testid="snooze-confirm" onClick={submitSnooze}>Snooze</Button>
          </div>
        </Modal>
      )}

      {logOpen && (
        <Modal title="Log contact" onClose={() => setLogOpen(false)}>
          <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)" }}>Action
            <input data-testid="log-action" value={action} onChange={(e) => setAction(e.target.value)} style={inputStyle} placeholder="e.g. Called site manager" />
          </label>
          <div style={{ height: 12 }} />
          <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)" }}>Note
            <textarea value={note} onChange={(e) => setNote(e.target.value)} style={{ ...inputStyle, minHeight: 70 }} />
          </label>
          <div className="flex" style={{ gap: 12, marginTop: 18, justifyContent: "flex-end" }}>
            <Button variant="secondary" onClick={() => setLogOpen(false)}>Cancel</Button>
            <Button data-testid="log-confirm" onClick={submitLog}>Save</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
